import type { PerspectiveCamera } from 'three';
import type { MouseLook } from '../core/MouseLook';
import { type Settings, saveSettings } from '../core/settings';
import { resetTuning, TUNING_DEFAULTS, tuning } from '../movement/tuning';

interface TuningField {
  key: keyof typeof tuning;
  label: string;
  step: number;
}

const FIELDS: TuningField[] = [
  { key: 'gravity', label: 'gravity', step: 25 },
  { key: 'airAccelerate', label: 'air accel', step: 5 },
  { key: 'groundAccelerate', label: 'ground accel', step: 0.5 },
  { key: 'friction', label: 'friction', step: 0.5 },
  { key: 'stopSpeed', label: 'stop speed', step: 5 },
  { key: 'maxSpeed', label: 'max speed', step: 10 },
  { key: 'airCap', label: 'air cap', step: 1 },
  { key: 'jumpVelocity', label: 'jump vel', step: 5 },
];

/**
 * T overlay (usable while the pointer is unlocked): live movement tuning plus
 * FOV/sensitivity settings. Movement values are session-only by design — the
 * keepers get committed to constants.ts; FOV/sensitivity persist via Settings.
 */
export class TuningPanel {
  private readonly el: HTMLElement;
  private readonly tuningInputs = new Map<keyof typeof tuning, HTMLInputElement>();
  visible = false;

  constructor(parent: HTMLElement, settings: Settings, look: MouseLook, camera: PerspectiveCamera) {
    this.el = document.createElement('div');
    this.el.id = 'tuning-panel';
    this.el.style.display = 'none';

    const title = document.createElement('h2');
    title.textContent = 'Tuning (Esc to free the mouse, T to close)';
    this.el.appendChild(title);

    for (const field of FIELDS) {
      this.tuningInputs.set(
        field.key,
        this.addRow(field.label, tuning[field.key], field.step, (value) => {
          tuning[field.key] = value;
        }),
      );
    }

    this.addRow('fov (deg)', settings.fov, 5, (value) => {
      settings.fov = Math.min(130, Math.max(60, value));
      camera.fov = settings.fov;
      camera.updateProjectionMatrix();
      saveSettings(settings);
    });
    this.addRow('sensitivity', settings.sensitivity, 0.0002, (value) => {
      if (value > 0 && value < 0.1) {
        settings.sensitivity = value;
        look.sensitivity = value;
        saveSettings(settings);
      }
    });

    const reset = document.createElement('button');
    reset.textContent = 'Reset movement to defaults';
    reset.addEventListener('click', () => {
      resetTuning();
      for (const [key, input] of this.tuningInputs) {
        input.value = String(TUNING_DEFAULTS[key]);
      }
    });
    this.el.appendChild(reset);

    parent.appendChild(this.el);
  }

  toggle(): void {
    this.visible = !this.visible;
    this.el.style.display = this.visible ? 'block' : 'none';
  }

  private addRow(
    label: string,
    initial: number,
    step: number,
    onChange: (value: number) => void,
  ): HTMLInputElement {
    const row = document.createElement('label');
    const span = document.createElement('span');
    span.textContent = label;
    const input = document.createElement('input');
    input.type = 'number';
    input.step = String(step);
    input.value = String(initial);
    input.addEventListener('change', () => {
      const value = Number(input.value);
      if (Number.isFinite(value)) {
        onChange(value);
      }
    });
    row.appendChild(span);
    row.appendChild(input);
    this.el.appendChild(row);
    return input;
  }
}
